import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UiStackControllerService } from 'src/app/cc-api/services';
import {
  urlDetectionPattern,
  whiteSpaceAllPattern,
  whiteSpaceInputPattern,
} from 'src/app/constants/inputPatterns';
import { CustomToastService } from 'src/app/pages/custom-toast/custom-toast.service';
import { ContextUpdatesService } from 'src/app/services/context-updates.service';
import { StackCreateService } from 'src/app/services/stack-create.service';
import { TokenExpiryService } from 'src/app/services/token-expiry.service';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import { UiAccountsControllerService } from 'src/app/cc-api/services';
import { Account, CreateBlueprintRequest, Stack } from 'src/app/cc-api/models';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { UiProjectTypeControllerService } from 'src/app/cc-api/services';
import { ProjectTypeSupportedIntentsComponent } from 'src/app/components/project-type-supported-intents/project-type-supported-intents.component';
import { NbDialogService } from '@nebular/theme';
import { cloudProviderOptions } from '../../../../constants/cloud-provider';

@Component({
  selector: 'app-create-project-form',
  templateUrl: './create-project-form.component.html',
})
export class CreateProjectFormComponent implements OnInit, OnDestroy {
  @Input() dialogRef;

  projectForm: UntypedFormGroup = new UntypedFormGroup({
    blueprintName: new UntypedFormControl('', [
      Validators?.required,
      Validators.pattern(whiteSpaceAllPattern),
    ]),
    vcsAccount: new UntypedFormControl('', [Validators.required]),
    creationType: new UntypedFormControl(false),
    selectedOrgName: new UntypedFormControl(''),
    description: new UntypedFormControl(''),
    draftClusters: new UntypedFormGroup({
      dev: new UntypedFormControl('true'),
      qa: new UntypedFormControl(''),
      prod: new UntypedFormControl('true'),
    }),
    projectType: new UntypedFormControl(''),
    relativePath: new UntypedFormControl('', [Validators.required]),
    gitReference: new UntypedFormControl('', [Validators.required]),
    gitUrl: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(whiteSpaceInputPattern),
      Validators.pattern(urlDetectionPattern),
    ]),
  });
  cloudProviderOptions: any;
  workspaceMap = {
    GITHUB: 'Organization',
    BITBUCKET: 'Workspace',
    GITLAB: 'Group',
  };
  accounts: Account[];
  accountsOptions: { label: string; value: string; provider: string }[] = [];
  envsOption = ['dev', 'qa', 'prod'];
  projectCreationInProgress: boolean = false;
  contextUpdates$: Subscription;
  vcsAccountId: string;
  loadingOrgNames: boolean = false;
  orgNames: { label: string; value: string }[] = [];
  orgName: string;
  selectedAccountProvider: string;
  selectedAccount: any;
  bitbucketProjectKey: UntypedFormControl = new UntypedFormControl({
    value: '',
    disabled: true,
  });
  appOrgMessage: string =
    "This is pre-selected based on your linked 'Git' account";
  allProjectTypes: any;
  allProjectTypesOptions: any;
  loadingProject: boolean = false;
  allowedClouds: any;
  projectTypeName: string;

  get blueprintName() {
    return this.projectForm.get('blueprintName');
  }

  get vcsAccount() {
    return this.projectForm.get('vcsAccount');
  }

  get creationType() {
    return this.projectForm.get('creationType');
  }

  get relativePath() {
    return this.projectForm.get('relativePath');
  }

  get gitUrl() {
    return this.projectForm.get('gitUrl');
  }

  get gitReference() {
    return this.projectForm.get('gitReference');
  }

  get selectedOrgName() {
    return this.projectForm.get('selectedOrgName');
  }

  get description() {
    return this.projectForm.get('description');
  }

  get draftClusters() {
    return this.projectForm.get('draftClusters');
  }

  get dev() {
    return this.draftClusters.get('dev');
  }
  get qa() {
    return this.draftClusters.get('qa');
  }
  get prod() {
    return this.draftClusters.get('prod');
  }
  get projectType() {
    return this.projectForm.get('projectType');
  }

  constructor(
    private router: Router,
    private stackController: UiStackControllerService,
    private stackCreateService: StackCreateService,
    private contextUpdatesService: ContextUpdatesService,
    private toastService: CustomToastService,
    private tokenExpiryService: TokenExpiryService,
    private accountControllerService: UiAccountsControllerService,
    public featureFlagService: FeatureFlagsService,
    private uiProjectTypeController: UiProjectTypeControllerService,
    private dialogService: NbDialogService,
  ) {}

  async ngOnInit() {
    try {
      await this.getAllAccounts();
    } catch (err) {}
    this.getAllProjectTypes();
    this.vcsAccount.valueChanges.subscribe(async (value) => {
      this.vcsAccountId = value;
      this.selectedAccount = this.accounts?.find(
        (account) => account?.id === value,
      );
      if (this.selectedAccount?.projectKey) {
        this.bitbucketProjectKey?.setValue(this.selectedAccount?.projectKey);
      }
      this.selectedAccount?.app
        ? this.selectedOrgName?.disable()
        : this.selectedOrgName?.enable();
      this.selectedAccountProvider = this.selectedAccount?.provider;
      if (this.selectedAccount?.provider === 'BITBUCKET') {
        this.selectedOrgName.setValidators([Validators.required]);
      } else {
        this.selectedOrgName.clearValidators();
      }
      if (!this.selectedAccount?.app) {
        this.getOrgsBasedOnAccountId();
      } else {
        const defaultOrgName = this.selectedAccount?.['orgName'] || 'PERSONAL';
        this.orgNames = [
          {
            label: 'Personal Account',
            value: 'PERSONAL',
          },
        ];
        setTimeout(() => {
          this.selectedOrgName.setValue(defaultOrgName);
        }, 0);
      }
    });
    this.selectedOrgName.valueChanges.subscribe((orgName) => {
      this.orgName = orgName;
    });
    this.onChangeProjectStatus(this.creationType?.value);
  }

  getAllAccounts() {
    return new Promise((resolve, reject) => {
      this.accountControllerService.getAllAccountsUsingGET().subscribe(
        (accountsList) => {
          this.accounts = accountsList;
          resolve(true);
        },
        (error) => {
          reject(false);
        },
      );
    });
  }

  createProject() {
    this.projectCreationInProgress = true;
    const requestBody = {
      blueprintName: this.blueprintName?.value,
      allowedClouds: this.allowedClouds,
      ...(this.creationType?.value !== false && {
        gitOpsRequest: {
          vcsUrl: this.gitUrl?.value,
          branch: this.gitReference?.value,
          relativePath: this.relativePath?.value,
        },
      }),
      vcsAccountId: this.vcsAccountId,
      orgName: this.orgName === 'PERSONAL' ? null : this.orgName,
      description: this.description?.value,
      draftClusters: this.getDraftClusters(this.draftClusters),
      projectTypeId: this.projectType?.value
        ? this.projectType?.value
        : this.allProjectTypes[0]?.id,
    };
    const httpsResponse =
      this.stackController.createProjectUsingPOST(requestBody);

    httpsResponse.subscribe(
      (stack) => {
        //Context switcher update after new project creation
        this.onProjectCreateSuccess(stack);
      },
      (error) => {
        this.toastService.error(
          error?.error?.message ||
            `The project ${this.blueprintName.value} couldn't be created. Please try again with proper values`,
        );
        this.projectCreationInProgress = false;
        triggerAnalyticsEvent(`PROJECTS_PROJECT_CREATE_PROJECT_UNSUCCESSFUL`);
      },
    );
  }

  onProjectCreateSuccess(stack) {
    this.contextUpdatesService.setLastOpenedBranchInLocalStorage(
      stack?.name,
      stack?.branch,
    );
    this.contextUpdatesService.requestContextSwitcherRefresh();
    this.contextUpdates$ =
      this.contextUpdatesService.contextSwitcherUpdated.subscribe((signal) => {
        if (signal.updated) {
          this.projectCreationInProgress = false;
          // When a stack is created, reinitialize the token-expiry service to reflect new mapping of stack and account
          this.tokenExpiryService.extractExpiringAndExpiredVCSAccounts();
          this.toastService.success(
            `The project ${this.blueprintName.value} has been created successfully.`,
          );
          this.dialogRef.close({ status: true, stack: stack });
          this.contextUpdates$.unsubscribe();
          triggerAnalyticsEvent(`PROJECTS_PROJECT_CREATE_PROJECT_SUCCESSFUL`, {
            blueprint: this.blueprintName?.value,
            draftClusters: this.draftClusters?.value,
          });
          this.router.navigate([
            '/',
            'stack',
            stack?.name,
            'blueprint-designer',
            this.contextUpdatesService.getLastBranchForBlueprint(stack?.name),
            'resource-list',
          ]);
        }
      });
  }

  sendProjectCreateEvent() {
    this.stackCreateService.sendStackEvent(true);
  }

  onClickHandleCancel() {
    triggerAnalyticsEvent(`PROJECTS_PROJECT_CREATE_PROJECT_CANCEL`);
    this.dialogRef.close();
  }

  getDraftClusters(draftClustersFormGroup: AbstractControl) {
    const draftClusters: any[] = [];

    Object.keys(draftClustersFormGroup.value).forEach((key) => {
      if (draftClustersFormGroup.value[key]) {
        draftClusters.push({
          clusterName: key,
          releaseStream: key?.toUpperCase(),
          stackName: this.blueprintName.value,
        });
      }
    });
    return draftClusters;
  }

  getOrgsBasedOnAccountId() {
    this.orgNames = [];
    this.loadingOrgNames = true;
    this.accountControllerService
      .getVcsOrganizationsByAccountIdUsingGET(this.vcsAccountId)
      .subscribe(
        (response) => {
          this.loadingOrgNames = false;
          const personalAccount = {
            label: 'Personal Account',
            value: 'PERSONAL',
          };
          const orgNames = response.map((name) => ({
            label: name,
            value: name,
          }));
          const account = this.accounts?.find(
            (account) => account?.id === this.vcsAccountId,
          );

          let defaultOrgName = account?.['orgName'];
          this.orgNames = [...orgNames];
          if (this.selectedAccountProvider !== 'BITBUCKET') {
            this.orgNames = [personalAccount, ...orgNames];
            defaultOrgName = account?.['orgName'] || 'PERSONAL';
          }
          this.selectedOrgName.setValue(defaultOrgName);
        },
        () => {
          this.loadingOrgNames = false;
        },
      );
  }

  afterAccountSave(accounts) {
    if (accounts?.length) {
      this.accounts = accounts;
    }
  }

  createCloudProviderOption(data: string): any {
    return {
      label: data,
      value: data,
      checked: true,
      icon: data?.toLowerCase(),
      iconPack: 'fci',
    };
  }

  getAllProjectTypes() {
    this.loadingProject = true;
    this.uiProjectTypeController.getAllProjectTypesUsingGET().subscribe(
      (data) => {
        this.allProjectTypes = data;
        if (data?.length === 1) {
          const selectedType = this.allProjectTypes[0];
          this.projectForm?.patchValue({
            projectType: selectedType?.id,
          });
          this.projectTypeName = selectedType?.name;
          this.cloudProviderOptions = [
            ...(selectedType?.allowedClouds || []),
          ].map((cloud) => this.createCloudProviderOption(cloud));
        }
        this.allProjectTypesOptions = data?.map((projectTypes) => ({
          label: projectTypes?.name,
          value: projectTypes?.id,
          allowedClouds: projectTypes?.allowedClouds,
          icon: 'info-outline',
          iconTooltip: 'Mapped modules',
        }));
        this.loadingProject = false;
      },
      (error) => {
        this.loadingProject = false;
      },
    );
  }

  onSelectProjectType(event) {
    let cloud = this.allProjectTypesOptions?.find(
      (option) => option?.value === event,
    )?.allowedClouds;
    this.allowedClouds = cloud?.map((data) => data);
    this.cloudProviderOptions = cloud?.map((data) =>
      this.createCloudProviderOption(data),
    );
  }

  onCloudSelect(event) {
    this.allowedClouds = event?.map(({ value }) => value);
  }

  showProjectTypeDetails(projectType) {
    let selectedProjectType = this.allProjectTypes.find(
      (pt) => pt?.id === projectType?.value,
    );
    this.dialogService.open(ProjectTypeSupportedIntentsComponent, {
      context: {
        projectType: selectedProjectType,
      },
    });
  }

  onChangeProjectStatus(event) {
    if (event === true) {
      this.projectForm?.patchValue({
        gitUrl: '',
        gitReference: '',
        relativePath: '',
      });
    } else if (event === false) {
      this.projectForm?.patchValue({
        gitUrl: 'https://github.com',
        gitReference: 'master',
        relativePath: '.',
      });
    }
  }

  ngOnDestroy() {
    this.allowedClouds = null;
    this.cloudProviderOptions?.forEach((permission) => {
      permission.checked = false;
    });
  }
}
